import React, { useEffect, useRef } from "react";
import gsap from "gsap";

const COLORS = {
  primary: "#3AAFA9",
  secondary: "#7BC8A4",
  heading: "#2E2E2E",
  text: "#4B5563",
  sectionTitle: "#1F7A75",
  background: "#3AAFA9",
};

const termsSections = [
  {
    title: "1. About Janak",
    text:
      "JANAK ELDERBRIDGE PRIVATE LIMITED (\"Janak\") is a healthcare support platform designed to connect families with reliable care services, health tracking, and assistance for everyday medical needs.",
  },
  {
    title: "2. Eligibility",
    text:
      "By using Janak, you confirm that you are at least 18 years old, the information you provide is accurate, and that you are authorized to use the service for yourself or someone under your legally authorized care.",
  },
  {
    title: "3. User Responsibilities",
    list: [
      "Use the platform only for lawful and genuine care-related purposes.",
      "Provide correct personal and medical details where required.",
      "Maintain respectful communication with support staff and service partners.",
      "Do not misuse the platform through spam, fraud, or harmful activity.",
    ],
  },
  {
    title: "4. Services and Availability",
    text:
      "Janak aims to provide consistent and timely support, but service availability may vary by location, emergency demand, network conditions, and maintenance schedules.",
  },
  {
    title: "5. Medical Disclaimer",
    text:
      "Janak provides care coordination and support services. It does not replace professional medical diagnosis, emergency treatment, or clinical advice from licensed practitioners. In life-threatening emergencies, always contact local emergency services directly.",
  },
  {
    title: "6. Payments and Subscriptions",
    list: [
      "Payments for services or subscriptions are processed securely through our authorized payment gateways (e.g., Cashfree).",
      "All fees are non-refundable unless explicitly stated in our Cancellation & Refund Policy.",
      "You agree to provide current and accurate billing information for all purchases made through Janak.",
    ]
  },
  {
    title: "7. Privacy and Data Protection",
    text:
      "Your data is handled according to our Privacy Policy, in compliance with the IT Act, 2000 and DPDP Act, 2023. We process data only for service delivery, support, and legal compliance.",
  },
  {
    title: "8. Account Suspension or Termination",
    text:
      "We may suspend or terminate access if terms are violated, false information is provided, abusive behavior occurs, or platform misuse impacts safety and service quality.",
  },
  {
    title: "9. Third-Party Services",
    text:
      "Janak may rely on trusted third-party providers for hosting, messaging, payments, analytics, and infrastructure. Their services are governed by their own policies and terms.",
  },
  {
    title: "10. Indemnification",
    text:
      "You agree to indemnify and hold harmless Janak, its affiliates, partners, and employees from any claim, demand, or damage, including reasonable attorneys' fees, arising out of your use of the platform or violation of these terms.",
  },
  {
    title: "11. Limitation of Liability",
    text:
      "To the extent permitted by law, Janak is not liable for indirect or incidental damages resulting from service interruptions, user misuse, or delays beyond reasonable control, including Force Majeure events.",
  },
  {
    title: "12. Changes to These Terms",
    text:
      "We may revise these Terms & Conditions from time to time. Continued use of Janak after updates constitutes acceptance of the revised terms.",
  },
  {
    title: "13. Contact Information",
    text:
      "If you have any questions regarding these Terms & Conditions, contact us at contact@thejanak.com or call +91 90981 14568.",
  },
];

const TermsConditions = () => {
  const sectionRef = useRef(null);
  const cardRef = useRef(null);
  const headingRef = useRef(null);
  const introRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
      tl.from(sectionRef.current, { opacity: 0, duration: 0.5 })
        .from(cardRef.current, { y: 36, opacity: 0, duration: 0.7 }, "-=0.2")
        .from(headingRef.current, { y: 18, opacity: 0, duration: 0.45 }, "-=0.2")
        .from(introRef.current, { y: 16, opacity: 0, duration: 0.45 }, "-=0.25")
        .from(
          ".terms-section-item",
          { y: 24, opacity: 0, stagger: 0.07, duration: 0.38 },
          "-=0.2",
        );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen overflow-hidden px-6 py-20"
      style={{ background: COLORS.background }}
    >
      <div
        className="absolute left-10 top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: `${COLORS.primary}33` }}
      />
      <div
        className="absolute bottom-20 right-10 h-72 w-72 rounded-full blur-3xl"
        style={{ background: `${COLORS.secondary}40` }}
      />

      <div
        ref={cardRef}
        className="relative mx-auto max-w-4xl rounded-3xl bg-white/80 px-6 py-12 text-gray-800 shadow-[0_30px_90px_rgba(0,0,0,0.15)] backdrop-blur-2xl md:px-12"
      >
        <h1
          ref={headingRef}
          className="mb-10 text-center text-4xl font-extrabold md:text-5xl"
          style={{ color: COLORS.heading }}
        >
          Terms & Conditions
        </h1>

        <p
          ref={introRef}
          className="mb-10 text-center text-base leading-relaxed md:text-lg"
          style={{ color: COLORS.text }}
        >
          Please read these Terms & Conditions carefully before using{" "}
          <span className="font-semibold" style={{ color: COLORS.primary }}>
            Janak
          </span>
          . By accessing or using our platform, you agree to comply with these terms.
        </p>

        <div className="space-y-9 text-[15.5px] leading-relaxed">
          {termsSections.map((section) => (
            <div key={section.title} className="terms-section-item">
              <h2
                className="mb-3 text-xl font-bold md:text-2xl"
                style={{ color: COLORS.sectionTitle }}
              >
                {section.title}
              </h2>
              {section.text ? <p style={{ color: COLORS.text }}>{section.text}</p> : null}
              {section.list ? (
                <ul className="list-disc space-y-1.5 pl-6" style={{ color: COLORS.text }}>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>

        <p className="mt-14 text-center text-sm text-gray-500">Last updated: July 2026</p>
      </div>
    </section>
  );
};

export default TermsConditions;
